import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BookOpen, Key } from 'lucide-react';
import { howItWorks } from '../../data/mock';

const iconMap = {
  'map-pin': MapPin,
  'book-open': BookOpen,
  'key': Key
};

const HowItWorksSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900 mb-4">
            Simple, not overwhelming
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Get exactly what you need—no more, no less
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {howItWorks.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            return (
              <div
                key={step.id}
                className={`text-center transition-all duration-700 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-stone-100 mb-6">
                  <IconComponent className="w-8 h-8 text-stone-700" strokeWidth={1.5} />
                </div>

                {/* Step Number */}
                <div className="text-sm font-medium text-amber-700 mb-3">
                  {index + 1}
                </div>

                {/* Title */}
                <h3 className="text-xl font-serif text-stone-900 mb-4">
                  {step.title}
                </h3>

                {/* Description */}
                <p className="text-stone-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-16 transition-all duration-700 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to="/destinations"
            className="inline-block px-8 py-4 bg-stone-900 text-white rounded-full font-medium hover:bg-stone-800 transition-colors"
          >
            Choose Your Destination
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
